import { useEffect, useMemo, useState } from 'react';

import { Button, Card, Col, Form, Modal, Row, InputGroup, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaTrash, FaPlus, FaSearch, FaChevronDown, FaChevronUp, FaUnlink } from 'react-icons/fa';
import useIsMobile from '../../utils/useIsMobile';
import '../../assets/styles/productEdit.css';

import Loader from '../../components/layout/Loader';
import {
    useGetProductContificoQuery,
    useUpdateProductContificoMutation,
} from '../../slices/contificoProductApiSlice';
import {
    useCreateProductMutation,
    useGetProductDetailsByCtfIdQuery,
    useUpdateProductMutation,
    useGetSuggestionsQuery,
} from '../../slices/productApiSlice';
import { useGetCategoriesQuery } from '../../slices/categoryApiSlice';
import { useGetBrandsQuery } from '../../slices/brandApiSlice';
import CategorySelect from '../../components/product/CategorySelect';
import BrandSelect from '../../components/product/BrandSelect';
import { compressAndUploadImage } from '../../utils/compressAndUploadImage';
import { useDeleteImageMutation } from '../../slices/utilsApiSlice';
import ImageCropper from '../../components/ui/ImageCropper';
import { sanitizeInput } from '../../utils/formUtils';

const VariationOptionRow = ({ option, vIndex, oIndex, product, handleOptionChange, removeOption, isMobile, excludedProductIds }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const hasLinkedProduct = Boolean(option?.linkedProduct);

    const linkedProductLabel =
        typeof option?.linkedProduct === 'object'
            ? option.linkedProduct?.name || option.name || ''
            : option?.name || '';

    const { data: suggestions, isLoading: loadingSuggestions } = useGetSuggestionsQuery(
        { keyword: searchTerm },
        { skip: hasLinkedProduct || !searchTerm || searchTerm.length < 3 }
    );

    // Filter out already-linked products (from other options) and the current product itself
    const filteredSuggestions = suggestions?.filter(
        (p) => !excludedProductIds || !excludedProductIds.has(String(p._id))
    ) ?? [];

    const availableImages = [
        ...(product.image && product.image !== 'img.jpg' ? [product.image] : []),
        ...(product.images || []),
    ];

    const handleUnlink = () => {
        handleOptionChange(vIndex, oIndex, 'linkedProduct', null);
        handleOptionChange(vIndex, oIndex, 'name', '');
    };

    const SuggestionDropdown = () =>
        !hasLinkedProduct && searchTerm && searchTerm.length >= 3 && filteredSuggestions && filteredSuggestions.length > 0 ? (
            <div
                className="position-absolute bg-white border rounded shadow-sm w-100 mt-1"
                style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}
            >
                {filteredSuggestions.map((p) => (
                    <div
                        key={p._id}
                        className="p-2 border-bottom suggestion-item"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            handleOptionChange(vIndex, oIndex, 'name', p.name);
                            handleOptionChange(vIndex, oIndex, 'linkedProduct', p._id);
                            handleOptionChange(vIndex, oIndex, 'image', p.image || '');
                            setSearchTerm('');
                        }}
                    >
                        <small>{p.name}</small>
                    </div>
                ))}
            </div>
        ) : null;

    /* ── Mobile: card layout ── */
    if (isMobile) {
        return (
            <Card className="mb-3 variation-option-card shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <span className="badge variation-option-badge px-2 py-1">Opción {oIndex + 1}</span>
                        <Button
                            variant="link"
                            className="variation-delete-btn p-0 text-danger"
                            onClick={() => removeOption(vIndex, oIndex)}
                            title="Eliminar opción"
                        >
                            <FaTrash size={18} />
                        </Button>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label column='sm' className="fw-semibold small mb-1 text-muted">Nombre</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ej: Rojo, XL, Vainilla..."
                            value={option.name}
                            onChange={(e) => handleOptionChange(vIndex, oIndex, 'name', e.target.value)}
                            disabled={hasLinkedProduct}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label column='sm' className="fw-semibold small mb-1 text-muted">Imagen</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Select
                                value={option.image}
                                onChange={(e) => handleOptionChange(vIndex, oIndex, 'image', e.target.value)}
                                disabled={hasLinkedProduct}
                                className="flex-grow-1"
                            >
                                <option value="">Sin imagen</option>
                                {availableImages.map((img, idx) => (
                                    <option key={idx} value={img}>Imagen {idx + 1}</option>
                                ))}
                            </Form.Select>
                            {option.image && (
                                <img
                                    src={option.image}
                                    alt="Vista previa"
                                    style={{ width: '44px', height: '44px', objectFit: 'cover', flexShrink: 0 }}
                                    className="rounded border"
                                />
                            )}
                        </div>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label column='sm' className="fw-semibold small mb-1 text-muted">Producto Vinculado</Form.Label>
                        <div className="position-relative">
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    placeholder="Buscar producto a vincular..."
                                    value={hasLinkedProduct ? linkedProductLabel : searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={hasLinkedProduct}
                                />
                                <InputGroup.Text>
                                    {loadingSuggestions ? (
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                    ) : (
                                        <FaSearch />
                                    )}
                                </InputGroup.Text>
                            </InputGroup>
                            <SuggestionDropdown />
                            {hasLinkedProduct ? (
                                <div className="mt-2 d-flex align-items-center justify-content-between px-3 py-2 rounded-2 linked-product-badge">
                                    <small className="text-success fw-bold">✓ Vínculo activo</small>
                                    <Button
                                        variant="link"
                                        className="p-0 text-danger d-flex align-items-center gap-1"
                                        onClick={handleUnlink}
                                    >
                                        <FaUnlink size={12} />
                                        <small>Quitar vínculo</small>
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </Form.Group>
                </Card.Body>
            </Card>
        );
    }

    /* ── Desktop: table row ── */
    return (
        <tr>
            <td>
                <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Nombre"
                    value={option.name}
                    onChange={(e) => handleOptionChange(vIndex, oIndex, 'name', e.target.value)}
                    disabled={hasLinkedProduct}
                />
            </td>
            <td>
                <Form.Select
                    size="sm"
                    value={option.image}
                    onChange={(e) => handleOptionChange(vIndex, oIndex, 'image', e.target.value)}
                    disabled={hasLinkedProduct}
                >
                    <option value="">Sin imagen</option>
                    {availableImages.map((img, idx) => (
                        <option key={idx} value={img}>
                            Imagen {idx + 1}
                        </option>
                    ))}
                </Form.Select>
                {option.image && (
                    <div className="mt-1">
                        <img src={option.image} alt="Preview" style={{ width: '30px', height: '30px', objectFit: 'cover' }} className="rounded" />
                    </div>
                )}
            </td>
            <td>
                <div className="position-relative">
                    <InputGroup size="sm">
                        <Form.Control
                            type="text"
                            placeholder="Buscar producto..."
                            value={hasLinkedProduct ? linkedProductLabel : searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={hasLinkedProduct}
                        />
                        <InputGroup.Text>
                            {loadingSuggestions ? (
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                            ) : (
                                <FaSearch />
                            )}
                        </InputGroup.Text>
                    </InputGroup>
                    <SuggestionDropdown />
                    {hasLinkedProduct && (
                        <div className="mt-1 d-flex align-items-center gap-1">
                            <small
                                className="text-success fw-bold"
                                title={
                                    typeof option.linkedProduct === 'object'
                                        ? option.linkedProduct._id
                                        : option.linkedProduct
                                }
                            >
                                Vinculo activo
                            </small>
                        </div>
                    )}
                </div>
            </td>
            <td className="text-center">
                <Button
                    variant="link"
                    className="text-danger p-0"
                    onClick={() => removeOption(vIndex, oIndex)}
                >
                    <FaTrash size={14} />
                </Button>
            </td>
        </tr>
    );
};

const safeParsePersonalizado1 = (personalizado1) => {
    if (!personalizado1) return {};
    if (typeof personalizado1 === 'object') {
        return personalizado1;
    }
    if (typeof personalizado1 === 'string') {
        try {
            const parsed = JSON.parse(personalizado1);
            return typeof parsed === 'object' && parsed !== null ? parsed : {};
        } catch (e) {
            return {};
        }
    }
    return {};
};

const normalizeVariations = (variations = []) => {
    if (!Array.isArray(variations)) return [];

    return variations.map((variation) => ({
        variationType: variation?.variationType || '',
        options: Array.isArray(variation?.options)
            ? variation.options.map((option) => ({
                  name: option?.name || '',
                  image: option?.image || '',
                  linkedProduct: option?.linkedProduct ?? null,
              }))
            : [],
    }));
};

const getContificoLockedFields = (cProduct) => ({
    contificoId: cProduct.id,
    contificoCode: cProduct.codigo,
    price: cProduct.pvp1,
    priceDist: cProduct.pvp4,
    taxPercentage: cProduct.porcentaje_iva,
    countInStock: cProduct.cantidad_stock,
});

const normalizeBrandId = (brandId) => {
    if (!brandId) return null;
    if (typeof brandId === 'object') {
        return brandId._id || brandId.id || null;
    }
    return String(brandId);
};

const PRODUCT_APPLICABILITY_OPTIONS = [
    {
        name: 'isFreeShippingApplicable',
        label: 'Apto para envío gratis',
        help: 'Incluye este producto en el envío gratis cuando la empresa lo ofrece a partir de un monto.',
    },
    {
        name: 'isGlobalDiscountApplicable',
        label: 'Apto para descuento global',
        help: 'Permite que los descuentos globales automáticos se apliquen a este producto.',
    },
];

const toCompanyRuc = (company) => {
    if (typeof company === 'string' && company) return company;
    if (company && typeof company === 'object' && typeof company.ruc === 'string') {
        return company.ruc;
    }
    return undefined;
};

/**
 * Collapsible section wrapper — collapses on mobile, always open on desktop.
 */
const MobileSection = ({ title, children, isMobile, headingClass = 'mb-3', defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    if (!isMobile) {
        return (
            <>
                <h5 className={headingClass}>{title}</h5>
                {children}
            </>
        );
    }

    return (
        <div className="mobile-section mt-3">
            <button
                type="button"
                className="mobile-section-btn w-100 d-flex justify-content-between align-items-center px-3 py-2 rounded-top"
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <span className="fw-bold fs-6">{title}</span>
                {isOpen ? <FaChevronUp size={13} /> : <FaChevronDown size={13} />}
            </button>
            {isOpen && <div className="mobile-section-body p-2">{children}</div>}
        </div>
    );
};

/**
 *
 * @param cProduct: Product from contifico API
 * @returns
 */
const ProductEditScreen = ({ cProduct, handleClose, refetch }) => {
    const { activeCompany } = useSelector((state) => state.admin);
    const [createProduct, { isLoading: loadingCreate }] = useCreateProductMutation();
    const [updateProduct, { isLoading: loadingUpdate }] = useUpdateProductMutation();
    const [updateProductContifico, { isLoading: loadingUpdateContifico }] =
        useUpdateProductContificoMutation();
    const [deleteImage] = useDeleteImageMutation();

    const isMobile = useIsMobile();

    const [showCropper, setShowCropper] = useState(false);
    const [rawImageSrc, setRawImageSrc] = useState(null);
    const [imageQueue, setImageQueue] = useState([]);

    const { data: categories, isLoading: loadingCategories } = useGetCategoriesQuery();
    const { data: brands = [], isLoading: loadingBrands } = useGetBrandsQuery();

    const personalizado1Details = safeParsePersonalizado1(cProduct.personalizado1);

    // Weight unit configuration
    const getWeightUnitInfo = (value) => {
        const weightUnits = {
            1: {
                icon: '🪶',
                label: 'Liviano',
                description: 'Ideal para artículos pequeños y livianos',
                class: 'small',
            },
            3: {
                icon: '📏',
                label: 'Estándar',
                description: 'Tamaño y peso promedio de productos',
                class: 'medium',
            },
            5: {
                icon: '⚖️',
                label: 'Pesado',
                description: 'Artículos pesados o de gran tamaño',
                class: 'large',
            },
            10: {
                icon: '🏋️',
                label: 'Muy Pesado',
                description: 'Productos muy pesados o voluminosos',
                class: 'very-large',
            },
            15: {
                icon: '🪑',
                label: 'Mueble pequeño',
                description: 'Mobiliario compacto, fácil de transportar',
                class: 'furniture-small',
            },
            30: {
                icon: '🛋️',
                label: 'Mueble',
                description: 'Mobiliario individual, requiere manipulación especial',
                class: 'furniture',
            },
            45: {
                icon: '🪑🛋️',
                label: 'Juego de Muebles',
                description: 'Set de mobiliario, volumen y peso elevados',
                class: 'furniture-set',
            },
            90: {
                icon: '🏗️',
                label: 'Set de mobiliario pesado',
                description: 'Set de mobiliario pesado, manipulación especializada',
                class: 'furniture-set',
            },
            180: {
                icon: '🏗️🏗️',
                label: 'Set de mobiliario muy pesado',
                description:
                    'Set de mobiliario muy pesado, logística y manipulación avanzada',
                class: 'furniture-set',
            },
        };
        return weightUnits[value] || weightUnits[3];
    };

    const {
        data: checkProduct,
        isLoading: loadingCheckExistance,
        isSuccess: productExists,
    } = useGetProductDetailsByCtfIdQuery(
        { ctfid: cProduct.id },
        { skip: !personalizado1Details.homeSantyDb }
    );

    const [onlinePvp, setOnlinePvp] = useState(personalizado1Details.onlinePvp || false);

    const [onlinePvpDist, setOnlinePvpDist] = useState(personalizado1Details.onlinePvpDist || false);

    const [homeSantyDb] = useState(personalizado1Details.homeSantyDb || false);

    // Existing HomeSanty products store the Contifico account on company.
    // Products that are not created yet still follow the sidebar company.
    const contificoCompany = homeSantyDb
        ? toCompanyRuc(checkProduct?.company) || toCompanyRuc(cProduct.company)
        : activeCompany?.ruc;

    // Always fetch fresh values from Contifico — this is the authoritative source
    const {
        data: freshContificoData,
        isLoading: loadingFreshContifico,
        isFetching: fetchingFreshContifico,
    } = useGetProductContificoQuery(
        { codigo: cProduct.codigo, company: contificoCompany },
        { skip: !cProduct.codigo || !contificoCompany }
    );

    // First element of the array response; fall back to cProduct until data arrives
    const freshCProduct = freshContificoData?.[0] ?? null;

    // Resolve primitives so useMemo deps are statically checkable
    const resolvedId = freshCProduct?.id ?? cProduct.id;
    const resolvedCodigo = freshCProduct?.codigo ?? cProduct.codigo;
    const resolvedPvp1 = freshCProduct?.pvp1 ?? cProduct.pvp1;
    const resolvedPvp4 = freshCProduct?.pvp4 ?? cProduct.pvp4;
    const resolvedIva = freshCProduct?.porcentaje_iva ?? cProduct.porcentaje_iva;
    const resolvedStock = freshCProduct?.cantidad_stock ?? cProduct.cantidad_stock;
    const resolvedBarcode = freshCProduct?.codigo_barra ?? cProduct.codigo_barra ?? '';

    const contificoLockedFields = useMemo(
        () => getContificoLockedFields(freshCProduct ?? cProduct),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [resolvedId, resolvedCodigo, resolvedPvp1, resolvedPvp4, resolvedIva, resolvedStock]
    );

    /**
     * @param product: HomeSantyDb Product Details to send as JSON in body request.
     */
    const [product, setProduct] = useState({
        ...contificoLockedFields,
        contificoBarcode: cProduct.codigo_barra ? cProduct.codigo_barra : '',
        priceAggregation: 0,
        name: cProduct.nombre,
        description: cProduct.descripcion,
        image: 'img.jpg',
        images: [],
        brandId: null,
        category: [],
        discount: 0,
        unitsPerBox: 0,
        weightUnit: 3,
        reserved_stock: 0,
        active: personalizado1Details.onlinePvp || false,
        activeDist: personalizado1Details.onlinePvpDist || false,
        isFreeShippingApplicable: true,
        isGlobalDiscountApplicable: true,
        variations: [],
        company: cProduct.company,
    });

    const handleInputChange = (name, value) => {
        setProduct((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const performTransaction = async (action) => {
        try {
            const productPayload = {
                ...product,
                ...contificoLockedFields,
                brandId: normalizeBrandId(product.brandId),
            };

            // Perform the first mutation
            const productId = cProduct.id;
            const homeSantyDetails = {
                onlinePvp,
                onlinePvpDist,
                homeSantyDb: true,
            };
            const updateProductContificoResult = await updateProductContifico({
                productId,
                data: { personalizado1: JSON.stringify(homeSantyDetails), company: product.company },
            }).unwrap();
            // Handle result of the second mutation if needed
            console.log('updateProductContifico result:', updateProductContificoResult);

            if (action === 'create') {
                // Perform the second mutation
                const createProductResult = await createProduct(productPayload).unwrap();
                // Handle result of the first mutation if needed
                console.log('createProduct result:', createProductResult);
            } else if (action === 'update') {
                const updateProductResult = await updateProduct(productPayload).unwrap();
                console.log('updateProduct result:', updateProductResult);
            }

            toast.success('Producto ' + updateProductContificoResult.nombre + ' creado con éxito');

            // If needed, update state or perform additional logic
        } catch (error) {
            // If any mutation fails, handle rollback
            console.error('Transaction failed:', error);
            // Implement rollback logic here if needed

            throw new Error('Transaction failed');
        }
    };

    const uploadFileHandler = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Add all files to the queue for sequential cropping
        setImageQueue(files);
        processNextInQueue(files);
    };

    const processNextInQueue = (queue) => {
        if (queue.length === 0) {
            setShowCropper(false);
            setRawImageSrc(null);
            return;
        }

        const nextFile = queue[0];
        const reader = new FileReader();
        reader.onload = () => {
            setRawImageSrc(reader.result);
            setShowCropper(true);
        };
        reader.readAsDataURL(nextFile);
    };

    const handleCropComplete = async (croppedImageFile) => {
        try {
            // Determine if this should be the main image
            let isReplacingMain;
            if (!product.image || product.image === 'img.jpg') {
                isReplacingMain = true;
            } else {
                isReplacingMain = window.confirm(
                    '¿Desea establecer esta imagen como la principal? (Cancelar para agregarla como adicional)'
                );
            }

            const fieldName = isReplacingMain ? 'image' : 'images';
            const response = await compressAndUploadImage(croppedImageFile, fieldName);
            toast.success(response.message || 'Imagen subida correctamente');

            const urls =
                response.images ||
                (Array.isArray(response.image) ? response.image : [response.image]);

            if (isReplacingMain) {
                if (product.image && product.image !== 'img.jpg') {
                    await deleteImage({ imageName: product.image });
                }
                setProduct((prev) => ({
                    ...prev,
                    image: urls[0],
                }));
            } else {
                setProduct((prev) => ({
                    ...prev,
                    images: [...prev.images, urls[0]],
                }));
            }

            // Move to next in queue
            const remainingQueue = imageQueue.slice(1);
            setImageQueue(remainingQueue);
            processNextInQueue(remainingQueue);
        } catch (error) {
            toast.error(error?.data?.message || error.message || 'Error al subir imagen');
            // Even on error, we might want to continue with the next image or stop
            const remainingQueue = imageQueue.slice(1);
            setImageQueue(remainingQueue);
            processNextInQueue(remainingQueue);
        }
    };

    const handleDeleteImage = async (imagePath, isMain = false) => {
        if (window.confirm('¿Está seguro de que desea eliminar esta imagen?')) {
            try {
                await deleteImage({ imageName: imagePath }).unwrap();
                if (isMain) {
                    setProduct((prev) => ({
                        ...prev,
                        image: prev.images.length > 0 ? prev.images[0] : 'img.jpg',
                        images: prev.images.length > 0 ? prev.images.slice(1) : [],
                    }));
                } else {
                    setProduct((prev) => ({
                        ...prev,
                        images: prev.images.filter((img) => img !== imagePath),
                    }));
                }
                toast.success('Imagen eliminada correctamente');
            } catch (error) {
                toast.error(error?.data?.message || error.message || 'Error al eliminar la imagen');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!product.category || product.category.length === 0) {
            return toast.error('Debe seleccionar al menos una categoría.');
        }

        if (product.discount < 0 || product.discount > 100) {
            return toast.error('El descuento debe estar entre 0 y 100');
        }

        for (const variation of product.variations || []) {
            if (!variation?.options?.length) continue;

            const firstOptionHasLinkedProduct = Boolean(variation.options[0]?.linkedProduct);
            const hasInvalidMix = variation.options.some(
                (option) => Boolean(option?.linkedProduct) !== firstOptionHasLinkedProduct
            );

            if (hasInvalidMix) {
                return toast.error(
                    `La variación "${variation.variationType || 'sin nombre'}" debe tener producto vinculado en todas las opciones o en ninguna.`
                );
            }
        }

        console.log('product: ' + product);
        if (!homeSantyDb) {
            try {
                await performTransaction('create');
                console.log('Transaction successful');
            } catch (err) {
                console.error('Transaction failed:', err);
                toast.error(err?.data?.message || err.message);
            }
        } else if (homeSantyDb) {
            try {
                await performTransaction('update');
                console.log('Transaction successful');
            } catch (err) {
                console.error('Transaction failed:', err);
                toast.error(err?.data?.message || err.message);
            }
        }
        refetch();
        handleClose();
    };

    const handleCategoryChange = (selectedCategoryIds) => {
        setProduct((prevProduct) => ({
            ...prevProduct,
            category: selectedCategoryIds,
        }));
    };

    const handleBrandChange = (selectedBrandId) => {
        setProduct((prevProduct) => ({
            ...prevProduct,
            brandId: selectedBrandId || null,
        }));
    };

    // Variation handlers
    const addVariation = () => {
        setProduct((prev) => ({
            ...prev,
            variations: [...(prev.variations || []), { variationType: '', options: [] }],
        }));
    };

    const removeVariation = (index) => {
        setProduct((prev) => ({
            ...prev,
            variations: (prev.variations || []).filter((_, vIndex) => vIndex !== index),
        }));
    };

    const handleVariationTypeChange = (index, value) => {
        setProduct((prev) => ({
            ...prev,
            variations: (prev.variations || []).map((variation, vIndex) =>
                vIndex === index ? { ...variation, variationType: value } : variation
            ),
        }));
    };

    const addOption = (variationIndex) => {
        setProduct((prev) => ({
            ...prev,
            variations: (prev.variations || []).map((variation, vIndex) =>
                vIndex === variationIndex
                    ? {
                          ...variation,
                          options: [
                              ...(variation.options || []),
                              { name: '', image: '', linkedProduct: null },
                          ],
                      }
                    : variation
            ),
        }));
    };

    const removeOption = (variationIndex, optionIndex) => {
        setProduct((prev) => ({
            ...prev,
            variations: (prev.variations || []).map((variation, vIndex) =>
                vIndex === variationIndex
                    ? {
                          ...variation,
                          options: (variation.options || []).filter(
                              (_, oIndex) => oIndex !== optionIndex
                          ),
                      }
                    : variation
            ),
        }));
    };

    const handleOptionChange = (variationIndex, optionIndex, field, value) => {
        setProduct((prev) => ({
            ...prev,
            variations: (prev.variations || []).map((variation, vIndex) => {
                if (vIndex !== variationIndex) return variation;

                return {
                    ...variation,
                    options: (variation.options || []).map((option, oIndex) => {
                        if (oIndex !== optionIndex) return option;

                        const updatedOption = { ...option, [field]: value };

                        if (field === 'linkedProduct') {
                            updatedOption.linkedProduct = value || null;
                            if (value) {
                                updatedOption.image = '';
                            }
                        }

                        return updatedOption;
                    }),
                };
            }),
        }));
    };

    useEffect(() => {
        if (productExists && checkProduct) {
            const hasMatchingContificoId =
                !checkProduct.contificoId || String(checkProduct.contificoId) === String(cProduct.id);

            if (!hasMatchingContificoId) {
                return;
            }

            setProduct({
                ...checkProduct,
                ...contificoLockedFields,
                brandId: normalizeBrandId(checkProduct.brandId),
                images: checkProduct.images || [],
                unitsPerBox: checkProduct.unitsPerBox || 0,
                weightUnit: checkProduct.weightUnit || 3,
                reserved_stock: checkProduct.reserved_stock || 0,
                isFreeShippingApplicable: checkProduct.isFreeShippingApplicable !== false,
                isGlobalDiscountApplicable: checkProduct.isGlobalDiscountApplicable !== false,
                variations: normalizeVariations(checkProduct.variations),
            });
        }
    }, [productExists, checkProduct, cProduct.id, contificoLockedFields]);

    useEffect(() => {
        setProduct((prev) => ({
            ...prev,
            ...contificoLockedFields,
            contificoBarcode: resolvedBarcode,
        }));
    }, [contificoLockedFields, resolvedBarcode]);

    // Build the set of already-selected linked-product IDs so other options hide those from suggestions.
    // Also include the current product's own HomeSanty _id so it never appears as a suggestion.
    const excludedProductIds = useMemo(() => {
        const ids = new Set();
        // Add the current product's own HomeSanty _id (if it already exists in the DB)
        if (checkProduct?._id) ids.add(String(checkProduct._id));
        // Add every linked product selected in any option across all variations
        (product.variations || []).forEach((variation) => {
            (variation.options || []).forEach((option) => {
                if (option.linkedProduct) {
                    const id =
                        typeof option.linkedProduct === 'object'
                            ? option.linkedProduct._id
                            : option.linkedProduct;
                    if (id) ids.add(String(id));
                }
            });
        });
        return ids;
    }, [product.variations, checkProduct]);

    return (
        <>
            {loadingCreate ||
            loadingUpdate ||
            loadingUpdateContifico ||
            loadingCheckExistance ||
            loadingCategories ||
            loadingBrands ||
            loadingFreshContifico ||
            fetchingFreshContifico ? (
                <Loader />
            ) : (
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            {/* Left Column: Images */}
                            <Col md={5} lg={4} className="border-bottom border-md-end pb-3 pb-md-0">
                                <MobileSection title="🖼️ Imágenes del Producto" isMobile={isMobile} headingClass="mb-3">
                                <Form.Group controlId="image" className="mb-4">
                                    <Form.Label column='lg' className="fw-bold">Imagen Principal</Form.Label>
                                    <div
                                        className="d-flex flex-wrap gap-2 mb-2 justify-content-center justify-content-md-start"
                                    >
                                        {product.image && product.image !== 'img.jpg' ? (
                                            <Card
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '300px',
                                                }}
                                                className="shadow-sm"
                                            >
                                                <Card.Img variant="top" src={product.image} />
                                                <Card.Body className="p-2 text-center">
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() =>
                                                            handleDeleteImage(product.image, true)
                                                        }
                                                        className="w-100"
                                                    >
                                                        <FaTrash className="me-1" /> Eliminar
                                                    </Button>
                                                </Card.Body>
                                            </Card>
                                        ) : (
                                            <div 
                                                className="border rounded d-flex align-items-center justify-content-center bg-light"
                                                style={{ width: '100%', maxWidth: '300px', height: '200px' }}
                                            >
                                                <span className="text-muted">Sin imagen principal</span>
                                            </div>
                                        )}
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label column='lg' className="fw-bold">Imágenes Adicionales</Form.Label>
                                    <div
                                        className="d-flex gap-2 mb-2 overflow-auto pb-2"
                                        style={{
                                            WebkitOverflowScrolling: 'touch',
                                        }}
                                    >
                                        {product.images && product.images.length > 0 ? (
                                            product.images.map((img, index) => (
                                                <Card
                                                    key={index}
                                                    style={{
                                                        minWidth: '100px',
                                                        width: '100px',
                                                    }}
                                                    className="shadow-sm"
                                                >
                                                    <Card.Img variant="top" src={img} />
                                                    <Card.Body className="p-1 text-center">
                                                        <Button
                                                            variant="link"
                                                            className="text-danger p-0"
                                                            onClick={() => handleDeleteImage(img)}
                                                        >
                                                            <FaTrash size={14} />
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            ))
                                        ) : (
                                            <small className="text-muted">No hay imágenes adicionales</small>
                                        )}
                                    </div>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label column='lg' className="btn btn-outline-primary w-100" style={{ cursor: 'pointer' }}>
                                        Subir Imágenes
                                        <Form.Control
                                            type="file"
                                            hidden
                                            multiple
                                            onChange={uploadFileHandler}
                                        />
                                    </Form.Label>
                                    <small className="text-muted d-block text-center">
                                        Formatos soportados: JPG, PNG. Máx 5MB.
                                    </small>
                                </Form.Group>
                                </MobileSection>
                            </Col>

                            {/* Right Column: Form Fields */}
                            <Col md={7} lg={8} className="ps-md-4">
                                <MobileSection title="📋 Información General" isMobile={isMobile} headingClass="mb-3">
                                <Row className="g-3">
                                    <Col xs={12} lg={8}>
                                        <Form.Group controlId="name">
                                            <Form.Label column='lg' className="fw-bold">Nombre del Producto</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Ingrese el Nombre"
                                                value={product.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} lg={4}>
                                        <Form.Group controlId="cod">
                                            <Form.Label column='lg' className="fw-bold">Código (Contifico)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                disabled
                                                value={product.contificoCode}
                                                className="bg-light"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Group controlId="description">
                                            <Form.Label column='lg' className="fw-bold">Descripción</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                placeholder="Ingrese la Descripción"
                                                value={product.description}
                                                onChange={(e) =>
                                                    handleInputChange('description', e.target.value)
                                                }
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="brandId">
                                            <Form.Label column='lg' className="fw-bold">Marca (opcional)</Form.Label>
                                            <BrandSelect
                                                brands={brands}
                                                selectedBrandId={normalizeBrandId(product.brandId)}
                                                onChange={handleBrandChange}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <Form.Group controlId="category">
                                            <Form.Label column='lg' className="fw-bold">Categorías</Form.Label>
                                            <CategorySelect
                                                categories={categories}
                                                selectedCategories={product.category}
                                                onChange={handleCategoryChange}
                                            />
                                            {product.category.length === 0 && (
                                                <Form.Text className="text-danger">
                                                    Debe seleccionar al menos una categoría
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                </MobileSection>

                                <MobileSection title="💰 Precios e Inventario" isMobile={isMobile} headingClass="mt-4 mb-3">
                                <Card className="bg-light border-0">
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col xs={6} md={3}>
                                                <Form.Group controlId="price">
                                                    <Form.Label column='sm' className="small fw-bold text-muted">Precio PVP</Form.Label>
                                                    <Form.Control type="text" disabled value={product.price} className="bg-white" />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Group controlId="priceDist">
                                                    <Form.Label column='sm' className="small fw-bold text-muted">Precio Dist.</Form.Label>
                                                    <Form.Control type="text" disabled value={product.priceDist} className="bg-white" />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Group controlId="taxPercentage">
                                                    <Form.Label column='sm' className="small fw-bold text-muted">IVA (%)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        disabled
                                                        value={product.taxPercentage}
                                                        className="bg-white"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={6} md={3}>
                                                <Form.Group controlId="countInStock">
                                                    <Form.Label column='sm' className="small fw-bold text-muted">Stock</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        disabled
                                                        value={product.countInStock}
                                                        className="bg-white"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group controlId="reserved_stock">
                                                    <Form.Label column='lg' className="fw-bold">Stock Reservado (Dañados/Exclusivos)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={product.reserved_stock}
                                                        onChange={(e) => {
                                                            handleInputChange(
                                                                'reserved_stock',
                                                                sanitizeInput(e.target.value, 'numeric')
                                                            );
                                                        }}
                                                        placeholder="0"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group controlId="unitsPerBox">
                                                    <Form.Label column='lg' className="fw-bold">Unidades por Caja</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={product.unitsPerBox}
                                                        onChange={(e) => {
                                                            handleInputChange(
                                                                'unitsPerBox',
                                                                sanitizeInput(e.target.value, 'numeric')
                                                            );
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group controlId="weightUnit">
                                                    <Form.Label column='lg' className="fw-bold d-flex align-items-center gap-2">
                                                        <span>📦</span>
                                                        <span>Unidad de Peso</span>
                                                    </Form.Label>
                                                    <Form.Select
                                                        value={product.weightUnit}
                                                        onChange={(e) => {
                                                            handleInputChange(
                                                                'weightUnit',
                                                                parseInt(e.target.value)
                                                            );
                                                        }}
                                                        className="fw-medium"
                                                    >
                                                        <option value={1}>🪶 Pequeño (1) - Artículo liviano</option>
                                                        <option value={3}>📏 Mediano (3) - Tamaño estándar</option>
                                                        <option value={5}>⚖️ Grande (5) - Artículo pesado</option>
                                                        <option value={10}>🏋️ Muy Grande (10) - Muy pesado o voluminoso</option>
                                                        <option value={15}>🪑 Mueble pequeño (15) - Mobiliario compacto</option>
                                                        <option value={30}>🛋️ Mueble (30) - Mobiliario individual</option>
                                                        <option value={45}>🪑🛋️ Juego de Muebles (45) - Set de mobiliario</option>
                                                        <option value={90}>🏗️ Set de mobiliario pesado (90)</option>
                                                        <option value={180}>🏗️🏗️ Set de mobiliario muy pesado (180)</option>
                                                    </Form.Select>
                                                    <div className={`weight-unit-description ${getWeightUnitInfo(product.weightUnit).class}`}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span style={{ fontSize: '1.25rem' }}>
                                                                {getWeightUnitInfo(product.weightUnit).icon}
                                                            </span>
                                                            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1 gap-sm-2 flex-grow-1">
                                                                <span className="fw-bold text-nowrap">
                                                                    {getWeightUnitInfo(product.weightUnit).label}
                                                                </span>
                                                                <span className="d-none d-sm-inline text-muted">•</span>
                                                                <small className="text-muted">
                                                                    {getWeightUnitInfo(product.weightUnit).description}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                            <Col xs={12} md={6}>
                                                <Form.Group controlId="discount">
                                                    <Form.Label column='lg' className="fw-bold">Descuento (%)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        value={product.discount}
                                                        onChange={(e) => {
                                                            handleInputChange(
                                                                'discount',
                                                                sanitizeInput(e.target.value, 'numeric')
                                                            );
                                                        }}
                                                        placeholder="0 - 100"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                                </MobileSection>

                                <MobileSection title="🚚 Envío y descuentos" isMobile={isMobile} headingClass="mt-4 mb-3">
                                <Card className="shadow-sm border-0">
                                    <Card.Body className="d-flex flex-column gap-3">
                                        {PRODUCT_APPLICABILITY_OPTIONS.map((option) => {
                                            const checked = product[option.name] === true;

                                            return (
                                                <div
                                                    key={option.name}
                                                    className={`product-applicability-option${checked ? ' is-checked' : ''}`}
                                                >
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={option.name}
                                                        className="mb-0"
                                                        checked={checked}
                                                        onChange={(event) =>
                                                            handleInputChange(option.name, event.target.checked)
                                                        }
                                                        label={
                                                            <span>
                                                                <strong>{option.label}</strong>
                                                                <br />
                                                                <small className="text-muted">{option.help}</small>
                                                            </span>
                                                        }
                                                    />
                                                </div>
                                            );
                                        })}
                                    </Card.Body>
                                </Card>
                                </MobileSection>

                                <MobileSection title="🛒 Disponibilidad en Tienda" isMobile={isMobile} headingClass="mt-4 mb-3">
                                <Card border={onlinePvp ? "primary" : "light"} className="shadow-sm">
                                    <Card.Body>
                                        <div className="d-flex flex-column gap-3">
                                            <Form.Check
                                                type="switch"
                                                id="onlinePvpSwitch"
                                                label={
                                                    <span>
                                                        <strong>🛒 Disponible Online PVP Público</strong>
                                                        <br />
                                                        <small className="text-muted">Mostrar producto a clientes regulares</small>
                                                    </span>
                                                }
                                                checked={onlinePvp}
                                                onChange={(e) => {
                                                    setOnlinePvp(e.target.checked);
                                                    handleInputChange('active', e.target.checked);
                                                }}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="onlinePvpDistSwitch"
                                                label={
                                                    <span>
                                                        <strong>🏷️ Disponible Online PVP Distribuidor</strong>
                                                        <br />
                                                        <small className="text-muted">Mostrar producto a distribuidores</small>
                                                    </span>
                                                }
                                                disabled={!onlinePvp}
                                                checked={onlinePvpDist}
                                                onChange={(e) => {
                                                    setOnlinePvpDist(e.target.checked);
                                                    handleInputChange('activeDist', e.target.checked);
                                                }}
                                            />
                                        </div>
                                    </Card.Body>
                                </Card>
                                </MobileSection>

                                <MobileSection title="🔀 Variaciones del Producto" isMobile={isMobile} headingClass="mt-4 mb-3">
                                <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-end'} mb-3`}>
                                    <Button
                                        variant={isMobile ? 'primary' : 'outline-primary'}
                                        size={isMobile ? 'md' : 'sm'}
                                        onClick={addVariation}
                                        className={isMobile ? 'w-100 py-2 shadow-sm' : ''}
                                    >
                                        <FaPlus className="me-1" />
                                        {isMobile ? 'Agregar tipo de variación' : 'Agregar Tipo'}
                                    </Button>
                                </div>

                                {product.variations?.length === 0 && (
                                    <p className="text-muted text-center py-3 small">
                                        Sin variaciones. Presiona el botón para agregar.
                                    </p>
                                )}

                                {product.variations?.map((variation, vIndex) => (
                                    <Card key={vIndex} className={`mb-3 shadow-sm ${isMobile ? 'variation-mobile-card' : 'border-secondary'}`}>
                                        <Card.Header className={`bg-light d-flex justify-content-between align-items-center ${isMobile ? 'py-3 variation-card-header-mobile' : 'py-2'}`}>
                                            <Form.Control
                                                type="text"
                                                placeholder="Ej: Color, Modelo, Tamaño..."
                                                value={variation.variationType}
                                                onChange={(e) => handleVariationTypeChange(vIndex, e.target.value)}
                                                className={`fw-bold ${isMobile ? 'flex-grow-1 me-2' : 'w-auto'}`}
                                                style={{ fontSize: isMobile ? '1rem' : '1.1rem' }}
                                                required
                                            />
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => removeVariation(vIndex)}
                                                className={`border-0 flex-shrink-0 ${isMobile ? 'p-2' : ''}`}
                                            >
                                                <FaTrash size={isMobile ? 16 : 14} />
                                            </Button>
                                        </Card.Header>
                                        <Card.Body className={isMobile ? 'p-2' : ''}>
                                            {isMobile ? (
                                                /* Mobile: card-based option list */
                                                <div className="variation-options-mobile">
                                                    {variation.options.length === 0 && (
                                                        <p className="text-muted text-center small py-2 mb-2">
                                                            Sin opciones aún
                                                        </p>
                                                    )}
                                                    {variation.options.map((option, oIndex) => (
                                                        <VariationOptionRow
                                                            key={oIndex}
                                                            option={option}
                                                            vIndex={vIndex}
                                                            oIndex={oIndex}
                                                            product={product}
                                                            handleOptionChange={handleOptionChange}
                                                            removeOption={removeOption}
                                                            isMobile={true}
                                                            excludedProductIds={excludedProductIds}
                                                        />
                                                    ))}
                                                    <Button
                                                        variant="outline-primary"
                                                        className="w-100 py-2 mt-1"
                                                        onClick={() => addOption(vIndex)}
                                                    >
                                                        <FaPlus className="me-2" /> Agregar Opción
                                                    </Button>
                                                </div>
                                            ) : (
                                                /* Desktop: table layout */
                                                <div className="table-responsive">
                                                    <Table hover size="sm" className="align-middle">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ minWidth: '150px' }}>Nombre</th>
                                                                <th style={{ minWidth: '120px' }}>Imagen</th>
                                                                <th style={{ minWidth: '180px' }}>Producto Vinculado</th>
                                                                <th className="text-center" style={{ width: '40px' }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {variation.options.map((option, oIndex) => (
                                                                <VariationOptionRow
                                                                    key={oIndex}
                                                                    option={option}
                                                                    vIndex={vIndex}
                                                                    oIndex={oIndex}
                                                                    product={product}
                                                                    handleOptionChange={handleOptionChange}
                                                                    removeOption={removeOption}
                                                                    isMobile={false}
                                                                    excludedProductIds={excludedProductIds}
                                                                />
                                                            ))}
                                                            <tr>
                                                                <td colSpan="5" className="p-0">
                                                                    <Button
                                                                        variant="link"
                                                                        className="w-100 text-decoration-none py-2"
                                                                        onClick={() => addOption(vIndex)}
                                                                    >
                                                                        <FaPlus className="me-1" /> Agregar Opción
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                ))}
                                </MobileSection>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className={`bg-light ${isMobile ? 'flex-column gap-2 p-3' : ''}`}>
                        <Button
                            variant="outline-secondary"
                            onClick={handleClose}
                            className={isMobile ? 'w-100 py-2' : 'px-4'}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            className={isMobile ? 'w-100 py-2 shadow-sm' : 'px-5 shadow-sm'}
                            disabled={loadingUpdate || loadingCreate || loadingUpdateContifico}
                        >
                            {loadingUpdate || loadingCreate || loadingUpdateContifico ? 'Guardando...' : 'Guardar Cambios'}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}
            <Modal
                show={showCropper}
                onHide={() => setShowCropper(false)}
                size="lg"
                centered
                fullscreen="sm-down"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Recortar Imagen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {rawImageSrc && (
                        <ImageCropper
                            imageSrc={rawImageSrc}
                            onCropComplete={handleCropComplete}
                            onCancel={() => {
                                const remainingQueue = imageQueue.slice(1);
                                setImageQueue(remainingQueue);
                                processNextInQueue(remainingQueue);
                            }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};
export default ProductEditScreen;
